<template>
  <section class="cfo-panel section-card" :class="scope.clsPrefix">
    <div class="ew-header">
      <div class="ew-title">能流分析</div>
    </div>
    <div v-if="loading" class="state-wrap loading-state">
      <i class="el-icon-loading state-icon"></i>
      <span class="state-text">数据加载中...</span>
    </div>
    <div v-else-if="error" class="state-wrap error-state">
      <!-- <svg class="state-icon error-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#F44F4F" stroke-width="1.5"/>
        <path d="M12 8v4M12 16h.01" stroke="#F44F4F" stroke-width="1.5" stroke-linecap="round"/>
      </svg> -->
      <span class="state-text">暂无能流分析数据</span>
      <!-- <button class="state-retry" @click="loadData">
        <i class="el-icon-refresh"></i>
        <span>重新加载</span>
      </button> -->
    </div>
    <div v-else ref="chartWrap" class="sankey-chart-wrap">
      <div :id="chartId" class="sankey-chart"></div>
    </div>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import { getToken } from '@/utils/auth'

/**
 * 节点层级 → 颜色梯度（按 level 区分能流环节）
 */
const LEVEL_COLORS = ['#4A5FF3', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4']

export default {
  name: 'CfoProfitLossSankey',
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cfo-pl-sankey'
      },
      chartId: 'cfo_sankey_chart_' + this._uid,
      chartInstance: null,
      chartData: null,
      resizeObserver: null,
      resizeTimer: null,
      loading: false,
      error: false
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.teardownResizeObserver()
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    /**
     * 加载能流桑基图数据：GET /energy-api/kx/ceo/flowShow（无参）
     */
    async loadData() {
      this.loading = true
      this.error = false
      try {
        const response = await axios.get('/energy-api/kx/ceo/flowShow', {
          headers: { token: getToken() }
        })
        const res = response.data || {}
        if (res.code !== 200) {
          throw new Error(res.msg || '能流数据接口返回异常')
        }
        const data = this.transformFlowData(res.data || {})
        if (!data.nodes.length) {
          throw new Error('能流数据为空')
        }
        this.chartData = data
        this.loading = false
        await this.$nextTick()
        this.drawChart(data)
        this.setupResizeObserver()
      } catch (e) {
        console.error('获取能流桑基图数据失败:', e)
        this.error = true
        this.loading = false
      }
    },
    /**
     * 将接口返回的能流结构转换为 ECharts sankey 所需 nodes/links
     * 节点 data.data：{ objNo, name, level, detail: [{ mediumName, value, unit }] }
     * 连线 data.links：{ source: objNo, target: objNo, hValue, medium, mediumColor, detail }
     * @param {Object} payload 接口 data 节点
     * @returns {{ nodes: Array, links: Array }}
     */
    transformFlowData(payload) {
      const rawNodes = Array.isArray(payload.data) ? payload.data : []
      const rawLinks = Array.isArray(payload.links) ? payload.links : []

      // 节点以 objNo 唯一标识，展示名 + 介质数值用于标签/tooltip
      const nodeMap = {}
      const nodes = rawNodes
        .filter(n => n && n.objNo)
        .map((n) => {
          const detailList = Array.isArray(n.detail)
            ? n.detail.map(d => ({
              mediumName: d.mediumName || '',
              // value 为 null 时视为无有效数值
              valueText: (d.value === null || d.value === undefined || d.value === '')
                ? ''
                : this.formatEnergyValue(d.value) + (d.unit ? ' ' + d.unit : '')
            }))
            : []
          // 标签仅展示第一个有数值的介质，无数值时只显示名称
          const primary = detailList.find(d => d.valueText)
          const valueText = primary ? ' ' + primary.valueText : ''
          const node = {
            name: n.objNo,
            displayName: n.name || n.objNo,
            displayLabel: (n.name || n.objNo) + valueText,
            level: n.level,
            detailList,
            itemStyle: {
              color: LEVEL_COLORS[((Number(n.level) || 1) - 1) % LEVEL_COLORS.length]
            }
          }
          nodeMap[n.objNo] = node
          return node
        })

      // 连线两端必须都存在于节点中
      // 注意：接口实际返回字段为小写 hvalue（与文档 hValue 不一致），两者兼容
      const links = rawLinks
        .filter(l => l && l.source && l.target && nodeMap[l.source] && nodeMap[l.target])
        .map((l) => {
          const detail = l.detail || {}
          const unit = detail.unit || ''
          // 真实值优先取 hValue/hvalue，其次 detail.value
          const rawValue = l.hValue !== undefined && l.hValue !== null
            ? l.hValue
            : (l.hvalue !== undefined && l.hvalue !== null ? l.hvalue : detail.value)
          const numValue = Math.abs(Number(rawValue))
          const hasValue = !Number.isNaN(numValue)
          const valueText = (hasValue ? this.formatEnergyValue(numValue) : '--') + (unit ? ' ' + unit : '')
          const link = {
            source: l.source,
            target: l.target,
            // 桑基图 value 为 0 无法绘制流量，用最小占位值 1 保证拓扑结构可见
            value: hasValue && numValue > 0 ? numValue : 1,
            isZeroValue: !hasValue || numValue === 0,
            medium: l.medium || detail.mediumName || '',
            valueText,
            sourceName: nodeMap[l.source].displayName,
            targetName: nodeMap[l.target].displayName,
            lineStyle: {
              color: l.mediumColor || '#4A5FF3'
            }
          }
          return link
        })

      return { nodes, links }
    },
    /**
     * 能源数值格式化：千分位，最多保留两位小数（不做万/亿换算，保留原始口径）
     * @param {string|number} val 数值
     * @returns {string}
     */
    formatEnergyValue(val) {
      const num = Number(val)
      if (val === null || val === undefined || val === '' || Number.isNaN(num)) {
        return '--'
      }
      return num.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    /**
     * 根据容器宽度计算图表响应式参数
     * 右侧预留足够空间，避免末级节点标签（如经营利润）被裁切
     * @param {number} width 容器宽度
     * @returns {{ height: number, fontSize: number, nodeGap: number, nodeWidth: number, left: number, right: number }}
     */
    getResponsiveConfig(width) {
      if (width < 720) {
        return { height: 220, fontSize: 9, nodeGap: 10, nodeWidth: 14, left: 4, right: 104 }
      }
      if (width < 1100) {
        return { height: 240, fontSize: 10, nodeGap: 12, nodeWidth: 16, left: 6, right: 124 }
      }
      return { height: 280, fontSize: 11, nodeGap: 14, nodeWidth: 18, left: 8, right: 140 }
    },
    /**
     * 渲染能流桑基图
     * @param {Object} data transformFlowData 转换后的 { nodes, links }
     */
    drawChart(data) {
      const dom = document.getElementById(this.chartId)
      if (!dom) return

      const wrap = this.$refs.chartWrap
      const width = (wrap && wrap.clientWidth) || dom.clientWidth || 1200
      const responsive = this.getResponsiveConfig(width)

      if (wrap) {
        wrap.style.height = responsive.height + 'px'
      }
      dom.style.height = responsive.height + 'px'

      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      this.chartInstance = echarts.init(dom)
      this.chartInstance.setOption({
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          confine: true,
          formatter: (params) => {
            if (params.dataType === 'edge') {
              const d = params.data || {}
              return d.sourceName + ' → ' + d.targetName +
                (d.medium ? '<br/>介质：' + d.medium : '') +
                '<br/>流量：' + d.valueText
            }
            const d = params.data || {}
            const details = (d.detailList || [])
              .filter(item => item.valueText)
              .map(item => (item.mediumName ? item.mediumName + '：' : '') + item.valueText)
              .join('<br/>')
            return d.displayName + (details ? '<br/>' + details : '')
          }
        },
        series: [{
          type: 'sankey',
          layout: 'none',
          emphasis: {
            focus: 'adjacency'
          },
          nodeAlign: 'justify',
          nodeGap: responsive.nodeGap,
          nodeWidth: responsive.nodeWidth,
          layoutIterations: 32,
          left: responsive.left,
          right: responsive.right,
          top: 10,
          bottom: 10,
          label: {
            fontSize: responsive.fontSize,
            color: '#64748b',
            position: 'right',
            distance: 6,
            formatter: (params) => (params.data && params.data.displayLabel) || params.name
          },
          lineStyle: {
            curveness: 0.5,
            opacity: 0.45
          },
          data: data.nodes,
          links: data.links
        }]
      }, true)
      this.$nextTick(() => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      })
    },
    /**
     * 监听容器尺寸变化，比仅监听 window 更适配工作台布局伸缩
     */
    setupResizeObserver() {
      this.teardownResizeObserver()
      const wrap = this.$refs.chartWrap
      if (!wrap || typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize()
      })
      this.resizeObserver.observe(wrap)
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    /**
     * 窗口/容器大小变化时自适应图表尺寸与密度
     */
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        if (!this.chartInstance || !this.chartData) return
        const wrap = this.$refs.chartWrap
        const width = (wrap && wrap.clientWidth) || 0
        if (!width) {
          this.chartInstance.resize()
          return
        }
        const responsive = this.getResponsiveConfig(width)
        if (wrap) {
          wrap.style.height = responsive.height + 'px'
        }
        const dom = document.getElementById(this.chartId)
        if (dom) {
          dom.style.height = responsive.height + 'px'
        }
        this.chartInstance.setOption({
          series: [{
            nodeGap: responsive.nodeGap,
            nodeWidth: responsive.nodeWidth,
            left: responsive.left,
            right: responsive.right,
            label: {
              fontSize: responsive.fontSize,
              color: '#64748b',
              position: 'right',
              distance: 6
            }
          }]
        })
        this.chartInstance.resize()
      }, 100)
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cfo-pl-sankey {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  padding: 16px;
  border-radius: 4px;

  .ew-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 10px;
    min-width: 0;
  }

  .ew-title {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e2226;
    line-height: 20px;
  }

  .ew-meta {
    text-align: right;
    min-width: 0;
  }

  .ew-meta-title {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    line-height: 1.4;
  }

  .ew-meta-sub {
    margin-top: 2px;
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.4;
  }

  .sankey-chart-wrap {
    width: 100%;
    height: 280px;
    // 数据节点多/标签长时允许横向滚动
    overflow-x: auto;
    overflow-y: hidden;
    // 保证容器至少 900px 宽，数据多时出现横向滚动条
    min-width: 900px;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;
  }

  .sankey-chart {
    width: 100%;
    height: 100%;
    // 图表内容最小宽度保证节点不被挤压
    min-width: 900px;
  }

  .state-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 280px;
    gap: 12px;
  }

  .state-icon {
    font-size: 28px;
    color: #4a5ff3;
  }

  .loading-state .state-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .error-icon {
    width: 36px;
    height: 36px;
    stroke: #F44F4F;
  }

  .state-text {
    font-size: 13px;
    color: #999;
  }

  .error-state .state-text {
    // color: #F44F4F;
  }

  .state-retry {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 16px;
    border: 1px solid #4a5ff3;
    border-radius: 4px;
    background: #fff;
    font-size: 12px;
    color: #4a5ff3;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f0f2ff;
    }
  }
}

.rvc-cosmoplat-workspace-cfo-pl-sankey .sankey-chart-wrap::-webkit-scrollbar {
  height: 6px;
}
.rvc-cosmoplat-workspace-cfo-pl-sankey .sankey-chart-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.rvc-cosmoplat-workspace-cfo-pl-sankey .sankey-chart-wrap::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-cfo-pl-sankey .sankey-chart-wrap::-webkit-scrollbar-thumb:hover {
  background: #b0b8c4;
}

@media screen and (max-width: 900px) {
  .rvc-cosmoplat-workspace-cfo-pl-sankey {
    .ew-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    .ew-meta {
      text-align: left;
    }
  }
}
</style>
